import React, { useState, useEffect } from 'react';
import { Tag, Plus, Trash2, Loader2, Info, Check } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';

interface TagSuggestion {
  name: string;
  color?: string;
}

const DEFAULT_COLORS = [
  { name: 'Green', value: '#22c55e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' },
];

const ClientTagsSettings: React.FC = () => {
  const [tags, setTags] = useState<TagSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#22c55e');
  const [error, setError] = useState<string | null>(null);
  const [deletingTag, setDeletingTag] = useState<string | null>(null);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.CLIENT_TAGS.LIST, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTags(data.tags || []);
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      setError('Failed to load tag suggestions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTagName.trim()) return;

    setSaving(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.CLIENT_TAGS.ADD, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newTagName.trim(),
          color: newTagColor,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setTags(data.tags || []);
        setNewTagName('');
        setNewTagColor('#22c55e');
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to add tag');
      }
    } catch (err) {
      setError('Failed to add tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTag = async (tagName: string) => {
    setDeletingTag(tagName);
    setError(null);
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.CLIENT_TAGS.DELETE(tagName), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTags(data.tags || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to delete tag');
      }
    } catch (err) {
      setError('Failed to delete tag');
    } finally {
      setDeletingTag(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary flex items-center gap-2">
          <Tag className="w-6 h-6" />
          Client Tags
        </h1>
        <p className="text-gray-600 dark:text-dark-text-secondary mt-1">
          Configure tag suggestions for categorizing clients. Staff can still create custom tags when adding them to clients.
        </p>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-medium">How Client Tags Work</p>
            <ul className="mt-1 space-y-1 text-blue-700 dark:text-blue-400">
              <li>• Tags help categorize clients (e.g., VIP, New Client, Referral)</li>
              <li>• Suggestions appear when staff add tags on client profiles</li>
              <li>• Staff can always type in custom tags not in this list</li>
              <li>• All tag changes are logged in the Client Audit</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Add New Tag Form */}
      <div className="mb-6 p-4 bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
          Add Tag Suggestion
        </h2>
        <form onSubmit={handleAddTag} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">
              Tag Name
            </label>
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="e.g., VIP, Referral, New Client"
              className="w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-dark-bg-tertiary dark:text-dark-text-primary"
              maxLength={50}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-2">
              Tag Color
            </label>
            <div className="flex flex-wrap gap-2">
              {DEFAULT_COLORS.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  onClick={() => setNewTagColor(color.value)}
                  className={`w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center ${
                    newTagColor === color.value
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                >
                  {newTagColor === color.value && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={saving || !newTagName.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Add Tag
            </button>
            
            {newTagName && (
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: newTagColor }}
              >
                Preview: {newTagName}
              </span>
            )}
          </div>
        </form>
      </div>

      {/* Current Tags List */}
      <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg">
        <div className="p-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">
            Tag Suggestions ({tags.length})
          </h2>
        </div>
        
        {tags.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-dark-text-muted">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">No tag suggestions yet</p>
            <p className="text-sm mt-1">Add your first tag suggestion above</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-dark-border">
            {tags.map((tag) => (
              <div
                key={tag.name}
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: tag.color || '#6b7280' }}
                  >
                    {tag.name}
                  </span>
                </div>
                <button
                  onClick={() => handleDeleteTag(tag.name)}
                  disabled={deletingTag === tag.name}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                  title="Remove tag suggestion"
                >
                  {deletingTag === tag.name ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientTagsSettings;
